// frontend/src/components/common/GlobalOverlay/PromotionBanner.js
import React, { useState, useEffect, useRef } from 'react';
import { X, Store, ChevronRight, Star, MapPin, Sparkles, TrendingUp, Award } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import useAnnouncementStore from '../../../store/announcementStore';
import { useOverlayAnimation } from './useOverlayAnimation';

// Rotation intervals in milliseconds
const ROTATION_INTERVALS = {
  ONE_MINUTE: 60 * 1000,
  TWO_MINUTES: 2 * 60 * 1000,
  THREE_MINUTES: 3 * 60 * 1000,
  FIVE_MINUTES: 5 * 60 * 1000,
  TEN_MINUTES: 10 * 60 * 1000,
  FIFTEEN_MINUTES: 15 * 60 * 1000,
  THIRTY_MINUTES: 30 * 60 * 1000,
  SIXTY_MINUTES: 60 * 60 * 1000,
};

const getRotationInterval = (interval) => {
  const map = {
    ONE_MINUTE: ROTATION_INTERVALS.ONE_MINUTE,
    TWO_MINUTES: ROTATION_INTERVALS.TWO_MINUTES,
    THREE_MINUTES: ROTATION_INTERVALS.THREE_MINUTES,
    FIVE_MINUTES: ROTATION_INTERVALS.FIVE_MINUTES,
    TEN_MINUTES: ROTATION_INTERVALS.TEN_MINUTES,
    FIFTEEN_MINUTES: ROTATION_INTERVALS.FIFTEEN_MINUTES,
    THIRTY_MINUTES: ROTATION_INTERVALS.THIRTY_MINUTES,
    SIXTY_MINUTES: ROTATION_INTERVALS.SIXTY_MINUTES,
  };
  return map[interval] || ROTATION_INTERVALS.FIVE_MINUTES;
};

const PromotionBanner = ({ autoHideDelay = 10000 }) => {
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState(false);
  const [rotationTimer, setRotationTimer] = useState(null);
  const [hideTimer, setHideTimer] = useState(null);
  const { isVisible: animVisible, animateIn, animateOut } = useOverlayAnimation(500);
  
  const {
    currentPromotion,
    isLoading,
    getCurrentPromotion,
    trackPromotionClick,
  } = useAnnouncementStore();

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (rotationTimer) clearInterval(rotationTimer);
      if (hideTimer) clearTimeout(hideTimer);
    };
  }, [rotationTimer, hideTimer]);

  // Load promotion on mount with delay
  useEffect(() => {
    const timer = setTimeout(() => {
      loadPromotion(true);
    }, 1000);
    return () => clearTimeout(timer);
  }, []);

  const loadPromotion = async (forceRefresh = false) => {
    if (!forceRefresh && currentPromotion) {
      showBanner();
      return;
    }

    try {
      const response = await getCurrentPromotion(forceRefresh);
      if (response?.data) {
        showBanner();
      } else {
        hideBanner();
      }
    } catch (error) {
      console.error('Failed to load promotion:', error);
      hideBanner();
    }
  };

  const showBanner = () => {
    setIsVisible(true);
    animateIn();

    if (hideTimer) clearTimeout(hideTimer);
    setHideTimer(setTimeout(() => {
      hideBanner();
    }, autoHideDelay));

    if (rotationTimer) clearInterval(rotationTimer);
    if (currentPromotion?.rotationInterval) {
      const interval = getRotationInterval(currentPromotion.rotationInterval);
      setRotationTimer(setInterval(() => {
        loadPromotion(true);
      }, interval));
    }
  };

  const hideBanner = () => {
    animateOut(() => {
      setIsVisible(false);
    });
    if (hideTimer) clearTimeout(hideTimer);
  };

  const handleClick = async () => {
    if (!currentPromotion) return;

    await trackPromotionClick(currentPromotion.id);

    if (currentPromotion.business?.id) {
      navigate(`/community/marketplace/business/${currentPromotion.business.id}`);
    } else if (currentPromotion.business?.slug) {
      navigate(`/community/marketplace/business/${currentPromotion.business.slug}`);
    }

    hideBanner();
  };

  const handleDismiss = () => {
    hideBanner();
  };

  if (!isVisible || !currentPromotion || isLoading) {
    return null;
  }

  const { business, title, description, imageUrl } = currentPromotion;
  const hasImage = !!imageUrl;

  return (
    <div className="bg-white rounded-xl shadow-2xl border border-primary-200/50 overflow-hidden transition-all duration-300 animate-slide-up relative group">
      {/* Premium top bar - Navy */}
      <div className="h-1 bg-gradient-to-r from-primary-700 via-primary-500 to-primary-700" />
      
      {/* Subtle decoration */}
      <div className="absolute top-1 right-4 opacity-40">
        <Award className="h-4 w-4 text-primary-400" />
      </div>
      
      <div className="p-3 flex items-center gap-3">
        {/* Business Logo with subtle shadow */}
        <div className="flex-shrink-0">
          {business?.logoUrl ? (
            <img 
              src={business.logoUrl} 
              alt={business.name}
              className="h-14 w-14 rounded-xl object-cover border-2 border-primary-100 shadow-md"
            />
          ) : (
            <div className="h-14 w-14 rounded-xl bg-gradient-to-br from-primary-600 to-primary-700 flex items-center justify-center shadow-md">
              <Store className="h-7 w-7 text-white" />
            </div>
          )}
        </div>
        
        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h4 className="text-sm font-bold text-gray-900">
              {title || business?.name || 'Promotion'}
            </h4>
            {business?.verified && (
              <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 bg-green-100 text-green-700 text-[9px] font-bold rounded-full">
                ✓ Verified
              </span>
            )}
            <span className="inline-flex items-center gap-0.5 px-2 py-0.5 bg-gradient-to-r from-primary-600 to-primary-700 text-white text-[8px] font-bold rounded-full shadow-md">
              <TrendingUp className="h-2.5 w-2.5" />
              PROMOTED
            </span>
          </div>
          
          {business && (
            <div className="flex items-center gap-3 mt-0.5 text-xs text-gray-600">
              {business.rating && (
                <span className="flex items-center gap-0.5 bg-amber-50 px-1.5 py-0.5 rounded-full">
                  <Star className="h-3 w-3 text-amber-400 fill-current" />
                  {business.rating.toFixed(1)}
                </span>
              )}
              {business.location && (
                <span className="flex items-center gap-0.5">
                  <MapPin className="h-3 w-3 text-gray-400" />
                  <span className="text-gray-500">{business.location}</span>
                </span>
              )}
              {business.category && (
                <span className="px-1.5 py-0.5 bg-primary-50 text-primary-700 rounded-full text-[9px] border border-primary-200">
                  {business.category}
                </span>
              )}
            </div>
          )}
          
          {description && (
            <p className="text-xs text-gray-700 mt-1 line-clamp-2">
              {description}
            </p>
          )}
          
          <button
            onClick={handleClick}
            className="mt-2 inline-flex items-center gap-1.5 px-3 py-1 bg-gradient-to-r from-primary-600 to-primary-700 text-white text-xs font-semibold rounded-lg transition-all shadow-md shadow-primary-500/30 hover:shadow-lg hover:shadow-primary-500/40 hover:scale-[1.02] active:scale-[0.98]"
          >
            View Business
            <ChevronRight className="h-3 w-3" />
          </button>
        </div>
        
        {/* Dismiss button */}
        <button
          onClick={handleDismiss}
          className="p-1 hover:bg-gray-100 rounded-lg transition-colors flex-shrink-0 self-start mt-1"
          aria-label="Dismiss promotion"
        >
          <X className="h-4 w-4 text-gray-400 hover:text-gray-600 transition-colors" />
        </button>
      </div>

      <style jsx>{`
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(-20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-slide-up {
          animation: slideUp 0.5s ease-out forwards;
        }
        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>
    </div>
  );
};

export default PromotionBanner;